import { useState, useMemo, useCallback, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { Grid } from "@mui/material";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { ICrewLogicModifier, IPairingLogicModifier, LogicModifierAllowableOnTypes } from "@vmosolutions/oc-interfaces";
import { LMGroupChip } from "src/components/LogicModifiers/LMGroupChip";
import moment from "moment-timezone";
import { LogicModifierForm } from "src/components/CrewSchedule/PairingEditorWindow/Forms/LogicModifierForm";

const initialValues: ICrewLogicModifier = {
  _id: undefined,
  logicModifier: {
    _id: undefined,
    title: "",
    description: "",
    color: "FFFFFF",
    isActive: false,
    createdAt: 0,
    createdBy: null,
    custom: {},
    allowableOnDay: false,
    allowableOnPairing: false,
    allowableOn: LogicModifierAllowableOnTypes.none,
  },
  crew_id: undefined,
  date: undefined,
  note: undefined,
  createdAt: undefined,
  createdBy: undefined,
};
interface ITimelineAddEvents {
  timelineContainer?: HTMLDivElement;
  manageSingleDay?: boolean;
  contextMenuForm?: boolean;
}

/**
 * CREW SCHEDULE - Manage Logic Modifiers
 * Displays a series of quicklinks to the flight builder component so that users can jump straight to the type of timeline event builder they want.
 */
export const ManageLMs = (props: ITimelineAddEvents): JSX.Element => {
  const dispatch = useDispatch();
  const { timelineContainer, manageSingleDay, contextMenuForm = false } = props;
  const selectedCrewDays = useSelector((state: IRootState) => state.crewSchedule.selectedCrewDays);
  const crewDayLogicModifiers = useSelector((state: IRootState) => state.crewSchedule.crewDayLogicModifiers);
  const [selectedAssignedCrewLM, setSelectedAssignedCrewLM] = useState<ICrewLogicModifier>(null);

  useEffect(() => {
    setSelectedAssignedCrewLM(null);
  }, [selectedCrewDays]);

  const assignedCrewLMs: ICrewLogicModifier[] = useMemo(() => {
    if (!selectedCrewDays || !crewDayLogicModifiers) return [];
    const { crewId, selectedTimes } = selectedCrewDays;
    if (!selectedTimes || selectedTimes?.length !== 1) return [];
    // key format truncates leading zeros to make sure they aren't required.
    const selectedDayKey = moment.utc(selectedTimes[0]).tz("Etc/UTC").format("YYYY-M-D");
    const LMs =
      crewDayLogicModifiers[crewId] && crewDayLogicModifiers[crewId][selectedDayKey]
        ? crewDayLogicModifiers[crewId][selectedDayKey]
        : [];
    return LMs;
  }, [selectedCrewDays, crewDayLogicModifiers]);

  // Handle assignment of business logic modifier to selected days.
  const handleUpdateLogicModifier = async (
    logicModifier: ICrewLogicModifier | IPairingLogicModifier,
    isNew: boolean
  ): Promise<void> => {
    const LMStartDateInUTC = selectedCrewDays.selectedTimes.map((startTime: number) => {
      const selectedDateMoment = moment
        .utc(startTime)
        .tz(selectedCrewDays.timezone ?? "Etc/UTC")
        .startOf("day");
      return selectedDateMoment.format();
    });
    // This is a utc version of the date that was selected in the crew sched view.
    // The time is ommitted as it does not matter in this context.The user selected a day by right
    // clicking and wanted to add a logic modifier to it. Month is 1 indexed, so that any future changes
    // to the way dates are handled (and the db)  are done in the backend alone.
    if (isNew) {
      const notifications: INotifications = {
        dispatch: dispatch,
        infoMessage: `Applying ${logicModifier?.logicModifier?.description ?? ""} to selected days.`,
        successMessage: `${logicModifier?.logicModifier?.description ?? ""} Assignment successful`,
        errorMessage: `Error: Unable to Assign ${logicModifier?.logicModifier?.description ?? ""}`,
      };
      await AmplifyAPIWrapper.Post(
        "/crewModifierSelectedDays",
        { ...logicModifier, crew_id: selectedCrewDays.crewId, selectedTimes: LMStartDateInUTC },
        notifications
      );
    } else {
      const notifications: INotifications = {
        dispatch: dispatch,
        infoMessage: "Updating Selected Crew Logic Modifier.",
        successMessage: "Crew Logic Modifier Successfully Updated",
        errorMessage: "Error: Unable to Update Crew Logic Modifier",
      };
      //@ts-ignore
      const updatedCLM: ICrewLogicModifier | null = await AmplifyAPIWrapper.Put(
        `/crewModifier/${selectedAssignedCrewLM._id}`,
        { ...logicModifier, crew_id: selectedCrewDays.crewId, selectedTimes: LMStartDateInUTC },
        notifications
      );
      if (updatedCLM?._id) {
        setSelectedAssignedCrewLM(updatedCLM);
      }
    }
  };

  /** Selected/deselect one of the assigned LMs */
  const handleLogicModifierClick = useCallback(
    (crewLogicModifier: any) => () => {
      const newAssignedCrewLM = crewLogicModifier?._id === selectedAssignedCrewLM?._id ? null : crewLogicModifier;
      setSelectedAssignedCrewLM(newAssignedCrewLM);
    },
    [selectedAssignedCrewLM]
  );

  /** Delete assigned LM */
  const handleDeleteLM = useCallback(
    (logicId: string) => {
      const deleteLM = async (): Promise<void> => {
        const notifications: INotifications = {
          dispatch: dispatch,
          infoMessage: "Removing Applied Crew Logic Modifier.",
          successMessage: "Crew Logic Modifier Successfully Removed",
          errorMessage: "Error: Unable to Remove Crew Logic Modifier",
        };
        const response: any = await AmplifyAPIWrapper.Del(`/crewModifier/${logicId}`, null, notifications);
        if (!response || response.deleted) {
          setSelectedAssignedCrewLM(null);
        }
      };
      deleteLM();
    },
    [dispatch]
  );

  const handleModifyExistingLM = useCallback(
    (index?: number) => (): void => {
      if (index !== undefined) {
        // If user clicks on X button of LM Chip
        const deleteLM: ICrewLogicModifier = assignedCrewLMs[index];
        if (deleteLM._id) handleDeleteLM(deleteLM._id);
      } else if (selectedAssignedCrewLM) {
        // If user selected an LM chip and clears selected menu
        if (selectedAssignedCrewLM._id) handleDeleteLM(selectedAssignedCrewLM._id);
      }
    },
    [assignedCrewLMs, handleDeleteLM, selectedAssignedCrewLM]
  );

  return (
    <Grid container spacing={0}>
      {manageSingleDay && (
        <Grid item xs={12}>
          {assignedCrewLMs?.length > 0 && (
            <LMGroupChip
              logicModifiers={assignedCrewLMs}
              handleDelete={handleModifyExistingLM}
              handleLogicModifierClick={handleLogicModifierClick}
              activeSelection={selectedAssignedCrewLM}
              logicModifierInRange={(Lms) => true}
            />
          )}
        </Grid>
      )}
      <Grid item xs={12}>
        <LogicModifierForm
          contextMenuForm={contextMenuForm}
          logicModifier={selectedAssignedCrewLM ?? initialValues}
          mosaicPane={timelineContainer}
          updateLogicModifier={handleUpdateLogicModifier}
        />
      </Grid>
    </Grid>
  );
};
